import { MPUT } from 'common/helpers.ts';
import type React from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import useSWRMutation from 'swr/mutation';
import type { Leg } from 'types/Leg.ts';
import { Button } from 'ui/component/Button.tsx';
import { Modal } from 'ui/component/Modal.tsx';
import { Input } from 'ui/control/Input.tsx';
import { getTranslatedError } from '#admin/helpers.ts';

type Props = {
  shipperReferenceIdLeg: Leg;
  onSave: () => void;
  onClose: () => void;
  isOpen: boolean;
};

export const ReferenceIdDialog: React.FC<Props> = ({ shipperReferenceIdLeg, onSave, onClose, isOpen }) => {
  const { t } = useTranslation();

  const formMethods = useForm({
    mode: 'onTouched',
    criteriaMode: 'all',
    shouldUnregister: true,
    shouldUseNativeValidation: false,
    shouldFocusError: true,
    defaultValues: {
      supplier_reference_id: shipperReferenceIdLeg.supplier_reference_id || '',
    },
  });

  const { handleSubmit, formState } = formMethods;
  const { trigger, isMutating, error } = useSWRMutation<any, any, string, { supplier_reference_id: string }>(`/leg/${shipperReferenceIdLeg.id}`, MPUT);

  const onSubmit = async ({ supplier_reference_id }) => {
    const value = supplier_reference_id.trim();
    await trigger({ supplier_reference_id: value ? value : null });
    onSave();
    onClose();
  };

  return (
    <Modal key={shipperReferenceIdLeg.id} title={<h4>{t('dialogs.referenceId.title')}</h4>} isOpen={isOpen} onClose={onClose}>
      <FormProvider {...formMethods}>
        <form className="flex w-[450px] flex-col gap-2.5" onSubmit={handleSubmit(onSubmit)}>
          <div className="mt-6 flex flex-col">
            <Input name="supplier_reference_id" label={`${t('dialogs.referenceId.fields.referenceId')}`} />
          </div>

          <div className="mt-6 flex gap-5">
            <Button disabled={isMutating} className="blue-outlined" onClick={onClose}>
              {t('actions.back')}
            </Button>
            <Button
              type="submit"
              disabled={formState.isSubmitting || !formState.isValid || !formState.isDirty}
              loading={formState.isSubmitting}
              className="blue flex items-center justify-center"
            >
              {t('actions.save')}
            </Button>
          </div>
          {error && <span className="text-center text-red-500">{getTranslatedError(error, t) || t('dialogs.referenceId.error')}</span>}
        </form>
      </FormProvider>
    </Modal>
  );
};
